'use client';

import { Box, CopyButton, Flex, Group, Stack, Text } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState } from 'react';

import { TrackedButton } from '@/components';
import { logger } from '@/utils';

interface QRCodeShareModalProps {
  issueUrl: string;
  issueTopic: string;
  issueLeader: string;
}

/**
 * Modal component for sharing issues via QR code
 *
 * @param issueUrl - The URL to encode in the QR code
 * @param issueTopic - The title of the issue (used for download filename)
 * @param issueLeader - The leader's name (used in the modal text)
 */
const QRCodeShareModal: React.FC<ContextModalProps<QRCodeShareModalProps>> = ({
  context: _context,
  id: _id,
  innerProps: { issueUrl, issueTopic, issueLeader },
}) => {
  const qrRef = useRef<SVGSVGElement>(null);

  const [logoData, setLogoData] = useState<string | null>(null);

  useEffect(() => {
    // Load and convert the logo to base64
    // If we don't do this conversion, the logo won't be displayed in the downloaded QR code
    const fetchLogo = async () => {
      try {
        const response = await fetch('/assets/logo.svg');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = () => {
          setLogoData(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        logger.error('Failed to load logo:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
    };

    fetchLogo();
  }, []);

  // Function to download QR code as SVG
  const downloadQRCodeAsSVG = () => {
    if (!qrRef.current) return;

    const svgElement = qrRef.current;
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const svgUrl = URL.createObjectURL(svgBlob);

    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = `${issueTopic.replace(/\s+/g, '-').toLowerCase()}-qrcode.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(svgUrl);
  };

  // Function to download QR code as PNG
  const downloadQRCodeAsPNG = () => {
    if (!qrRef.current) return;

    const svgElement = qrRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);

    // Set canvas dimensions (slightly larger than the QR code)
    canvas.width = 300;
    canvas.height = 300;

    // Create an image from the SVG
    const img = new Image();
    const svgBlob = new Blob([svgData], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const svgUrl = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert canvas to PNG
      const pngUrl = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');

      // Download PNG
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${issueTopic.replace(/\s+/g, '-').toLowerCase()}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(svgUrl);
    };

    img.src = svgUrl;
  };

  return (
    <Stack
      align="center"
      gap="md"
      p="sm"
    >
      <Text variant="body">
        Vote with <b>{issueLeader}</b> on{' '}
        <Text
          component="span"
          variant="body-bold-underlined"
          inherit
        >
          {issueTopic}
        </Text>
      </Text>

      <Box
        p="md"
        bg="white"
        style={{ borderRadius: '8px', width: '232px' }}
      >
        <QRCodeSVG
          ref={qrRef}
          value={issueUrl}
          size={200}
          level="H" // High error correction for logo
          bgColor="#FFFFFF"
          fgColor="var(--mantine-color-primary-7)"
          imageSettings={
            logoData
              ? {
                  src: logoData, // Use the base64 data
                  x: undefined,
                  y: undefined,
                  height: 40,
                  width: 40,
                  excavate: true,
                }
              : undefined
          }
        />
      </Box>

      <Group
        justify="center"
        gap="md"
        style={{ width: '100%', marginTop: '10px' }}
      >
        <TrackedButton
          variant="secondary-light"
          onClick={downloadQRCodeAsSVG}
          trackingName="QRCodeShareModal_download_svg"
          style={{ flexGrow: 1 }}
        >
          Download SVG
        </TrackedButton>
        <TrackedButton
          variant="secondary-light"
          onClick={downloadQRCodeAsPNG}
          trackingName="QRCodeShareModal_download_png"
          style={{ flexGrow: 1 }}
        >
          Download PNG
        </TrackedButton>
      </Group>

      <Flex
        style={{ width: '100%' }}
        justify="space-between"
        align="center"
        gap="0px"
        direction="row"
      >
        <CopyButton value={`${issueUrl}`}>
          {({ copied, copy }) => (
            <TrackedButton
              variant="primary"
              size="sm"
              style={{
                textOverflow: 'ellipsis',
              }}
              onClick={() => {
                copy();
              }}
              fullWidth
              trackingName="QRCodeShareModal_copy_link"
            >
              {copied ? 'Copied Link' : `Copy Link: ${issueUrl}`}
            </TrackedButton>
          )}
        </CopyButton>
      </Flex>
    </Stack>
  );
};

export { QRCodeShareModal };
